import type { ReactNode } from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import type { MyHole, RoundHole } from '../../lib/rounds.ts';
import type { Rendus } from '../../lib/scoreNet.ts';

/* La carte de score en bandeau, comme à la télévision.
 *
 * ── Pourquoi des formes et pas seulement des chiffres ──────────────────────
 *
 * Un 4 sur un par 3 et un 4 sur un par 5 n'ont rien à voir, et le chiffre seul
 * ne le dit pas — il faudrait connaître le par de chaque trou pour lire la
 * ligne. La forme porte l'écart : cercle en dessous du par, carré au-dessus,
 * rien à égalité. C'est la convention des retransmissions depuis toujours, et
 * elle se lit sans légende parce que tout le monde l'a déjà vue.
 *
 * Le remplissage marque le cran suivant : cercle plein pour l'eagle, carré
 * plein pour le double. Un joueur voit d'un coup d'œil la texture de son tour
 * avant même d'avoir lu un seul chiffre.
 *
 * ── Pourquoi la couleur ne suffit pas ──────────────────────────────────────
 *
 * Vert et rouge sont indiscernables pour huit pour cent des hommes. La forme
 * dit donc la même chose que la couleur, et l'une comme l'autre suffit : c'est
 * la règle de toute l'application, pas une précaution ajoutée ici.
 *
 * ── Le défilement suit le joueur ───────────────────────────────────────────
 *
 * Dix-huit trous ne tiennent pas sur un téléphone. Le bandeau se centre tout
 * seul sur le trou courant à chaque changement — on ne cherche jamais où l'on
 * est. Le mouvement est doux, sauf pour qui a demandé moins d'animations.
 */

interface Props {
    holes: RoundHole[];
    myHoles: MyHole[];
    /** Les coups rendus à ce joueur, trou par trou. Absent : la carte est lue
     *  en brut et ne montre aucun point. */
    rendus?: Rendus;
    /** Lire la carte en NET. Sans `rendus` calculables, le brut reste. */
    net?: boolean;
    /** Le trou en cours, souligné. Absent sur un relevé de fin de tour : la
     *  partie est jouée, aucun trou n'est « en cours ». */
    trouCourant?: number;
    /** Absent, les cases cessent d'être des boutons — un relevé ne se
     *  navigue pas. */
    onChoisir?: (n: number) => void;
    /** Le ruban rend son habillage de carte.
     *
     *  Sur l'écran de jeu il n'est plus un objet à lui seul mais une ligne de
     *  l'étape « Le trou », posée sous le panneau vert dans la même carte :
     *  une carte dans une carte y aurait ajouté un cadre et une ombre pour ne
     *  rien séparer de plus. Ailleurs — relevé d'un tour terminé, profil d'un
     *  partenaire — il reste un bloc autonome et garde son cadre. */
    nu?: boolean;
    /** Le ruban est posé sur le panneau vert profond, et non sur du papier.
     *
     *  Il devient alors le bandeau de tête de l'écran de jeu : c'est LE tour
     *  qu'on y lit, avant tout le reste. Les couleurs des scores basculent sur
     *  leurs jumeaux clairs — voir `allure`. */
    sombre?: boolean;
}

/* Les coups rendus sur un trou, en points.
 *
 * Un chiffre de plus dans la case aurait disputé la place au score, qui est ce
 * qu'on vient lire. Des points se comptent d'un coup d'œil sans être lus — un,
 * deux, trois — et le golf les écrit déjà ainsi sur les cartes papier.
 *
 * La rangée garde sa hauteur même à zéro : sans cela le ruban se décalerait
 * d'un trou à l'autre, et les cases cesseraient d'être alignées.
 */
function PointsRendus({ n, sombre }: { n: number; sombre: boolean }) {
    return (
        <span className="flex h-1.5 items-center gap-0.5" aria-hidden="true">
            {Array.from({ length: n }, (_, i) => (
                <span key={i} className="size-1.5 rounded-full"
                    style={{ background: sombre ? 'var(--podium-or)' : 'var(--brass-ink)' }} />
            ))}
        </span>
    );
}

/** Une case : bouton quand on peut y aller, simple bloc sur un relevé. */
interface CaseProps {
    interactif: boolean;
    onClick: () => void;
    children: ReactNode;
    'aria-label': string;
    'aria-current'?: 'true';
}

const Case = forwardRef<HTMLButtonElement, CaseProps>(
    ({ interactif, onClick, children, ...aria }, ref) => {
        const classe = 'flex w-8 shrink-0 flex-col items-center gap-0.5';
        return interactif
            ? <button ref={ref} type="button" onClick={onClick} className={classe} {...aria}>{children}</button>
            : <span className={classe} {...aria}>{children}</span>;
    },
);
Case.displayName = 'Case';

/** L'allure d'une case selon l'écart au par.
 *
 *  Exportée : la modale des trous reprend exactement le même vocabulaire de
 *  formes. Deux copies de ces règles finiraient par diverger, et un cercle
 *  qui ne voudrait pas dire la même chose à deux endroits de l'écran serait
 *  pire que pas de forme du tout. */
export function allure(ecart: number | null, sombre = false): string {
    /* Les mêmes formes, deux jeux de couleurs.
     *
     * Le ruban vit désormais sur le panneau vert profond en haut de l'écran de
     * jeu, et sur du papier partout ailleurs — relevé de fin de tour, liste
     * des trous, profil d'un partenaire. Les verts et rouges du thème sont
     * taillés pour écrire sur le papier : à 46 et 50 % de clarté ils tombent
     * sous 1,5:1 sur le vert. D'où les jumeaux clairs, --score-gagne et
     * --score-perdu, déclarés dans index.css.
     *
     * Ce sont bien les mêmes RÈGLES : cercle sous le par, carré au-dessus,
     * plein pour le cran suivant. Un seul endroit les décrit, sans quoi deux
     * rubans finiraient par ne plus dire la même chose. */
    if (sombre) {
        if (ecart == null) return 'border border-dashed border-white/25 text-white/40';
        if (ecart <= -2) return 'rounded-full bg-[var(--score-gagne)] text-[color:var(--fairway-deep)] font-bold';
        if (ecart === -1) return 'rounded-full border-2 border-[var(--score-gagne)] text-[color:var(--score-gagne)] font-bold';
        if (ecart === 0) return 'text-white';
        if (ecart === 1) return 'border-2 border-[var(--score-perdu)] text-[color:var(--score-perdu)] font-bold';
        return 'bg-[var(--score-perdu)] text-[oklch(25%_0.06_25)] font-bold';
    }
    if (ecart == null) return 'border border-dashed border-base-content/20 opacity-50';
    if (ecart <= -2) return 'rounded-full bg-success text-success-content font-bold';
    if (ecart === -1) return 'rounded-full border-2 border-success text-success font-bold';
    if (ecart === 0) return 'text-base-content';
    if (ecart === 1) return 'border-2 border-error text-error font-bold';
    return 'bg-error text-error-content font-bold';
}

export function Carte({
    holes, myHoles, trouCourant, onChoisir, nu = false, sombre = false,
    rendus, net = false,
}: Props) {
    const interactif = onChoisir != null;
    const ruban = useRef<HTMLDivElement>(null);
    const courant = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const cible = courant.current;
        if (!cible || !ruban.current) return;
        const bande = ruban.current.getBoundingClientRect();
        const case_ = cible.getBoundingClientRect();

        /* Le ruban se recentre À CHAQUE trou, même quand la case est déjà
         * visible. Le mouvement est ce qui donne au bandeau sa vie : on VOIT
         * le tour avancer sous ses doigts, au lieu d'un ruban figé qui ne
         * bouge que lorsqu'il y est forcé.
         *
         * Le premier essai ne défilait qu'en cas de débordement, pour éviter
         * une case coupée au bord — mais la coupure venait d'une marge de deux
         * pixels, corrigée depuis. Le remède visait le mauvais mal.
         *
         * `scrollIntoView` est écarté : il ferait aussi défiler la PAGE. */
        const delta = (case_.left + case_.width / 2) - (bande.left + bande.width / 2);
        const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        ruban.current.scrollBy({ left: delta, behavior: doux ? 'smooth' : 'auto' });
    }, [trouCourant]);

    /* Le NET ne s'affiche que s'il est calculable.
     *
     * Il demande l'index du joueur ET l'ordre de difficulté des trous. Sans
     * l'un des deux, afficher un net reviendrait à inventer une répartition :
     * la carte retombe sur le brut, et l'écran qui porte la bascule dit
     * pourquoi. */
    const enNet = net && rendus != null && rendus.total != null && rendus.indexConnus;
    const rendusDe = (n: number): number => (enNet ? rendus?.parTrou.get(n) ?? 0 : 0);

    const total = holes.reduce((s, h) => {
        const j = myHoles.find((m) => m.holeNumber === h.number);
        if (j?.strokes == null) return s;
        return s + j.strokes - rendusDe(h.number);
    }, 0);
    const parJoue = holes
        .filter((h) => myHoles.some((m) => m.holeNumber === h.number && m.strokes != null))
        .reduce((s, h) => s + (h.par ?? 0), 0);

    return (
        <div className={`flex items-stretch gap-2 ${nu
            ? ''
            : 'rounded-box border border-base-300 bg-base-100 px-2 py-1.5 shadow-sm'}`}>
            {/* De l'air en haut et en bas du ruban.

                L'anneau du trou courant deborde de trois pixels, et
                `overflow-x: auto` coupe AUSSI en vertical — la regle CSS veut
                qu'un axe non visible force l'autre a `auto`. Sans ce
                rembourrage, le trait du bas etait rogne net. */}
            {/* De l'air sur les quatre cotes du ruban.

                Sans marge laterale, la premiere case n'avait que deux pixels
                avant le bord : un cercle plein s'y lisait comme coupe, alors
                qu'il ne l'etait pas. Deux pixels de plus ne changent rien a
                l'encombrement et beaucoup a la lecture.

                Barre de defilement masquee.

                Elle traversait le bas des cases et se lisait comme un trait de
                la carte. Le ruban se centre tout seul et chaque case est
                touchable : la barre n'apprenait rien qu'on ne voie deja. Sur
                mobile elle est de toute facon en surimpression et s'efface. */}
            <div ref={ruban}
                className="flex flex-1 gap-1 overflow-x-auto px-1 py-1 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {holes.map((h) => {
                    const joue = myHoles.find((m) => m.holeNumber === h.number);
                    const rendu = rendus?.parTrou.get(h.number) ?? 0;
                    const coups = joue?.strokes == null
                        ? null
                        : joue.strokes - rendusDe(h.number);
                    // L'allure suit le score AFFICHÉ : en net, un six rendu à
                    // cinq sur un par 5 est un par, et doit se lire comme tel.
                    const ecart = coups != null && h.par != null ? coups - h.par : null;
                    const ici = h.number === trouCourant;
                    return (
                        <Case
                            key={h.number}
                            ref={ici ? courant : undefined}
                            interactif={interactif}
                            onClick={() => onChoisir?.(h.number)}
                            aria-label={`Trou ${h.number}${joue?.strokes != null ? `, ${joue.strokes} coups` : ''}${
                                rendu > 0 ? `, ${rendu} coup${rendu > 1 ? 's' : ''} rendu${rendu > 1 ? 's' : ''}` : ''}${
                                enNet && coups != null ? `, net ${coups}` : ''}`}
                            aria-current={ici ? 'true' : undefined}
                        >
                            <span className={`text-[10px] leading-none tabular-nums ${
                                ici
                                    ? sombre ? 'font-bold text-accent' : 'font-bold text-primary'
                                    : sombre ? 'text-white/45' : 'opacity-50'
                            }`}>
                                {h.number}
                            </span>
                            <span className={`flex size-7 items-center justify-center text-xs tabular-nums ${allure(ecart, sombre)} ${
                                // Le trou courant se souligne, il ne change pas
                                // de forme : la forme dit le score, et rien
                                // d'autre ne doit s'en emparer. Sur le panneau
                                // vert l'anneau passe en laiton : le vert
                                // primaire y disparaitrait dans le fond.
                                ici
                                    ? sombre
                                        ? 'ring-2 ring-accent ring-offset-1 ring-offset-[var(--fairway-deep)]'
                                        : 'ring-2 ring-primary ring-offset-1 ring-offset-base-100'
                                    : ''
                            }`}>
                                {coups ?? ''}
                            </span>
                            {/* Les coups rendus, sous la case : ils appartiennent
                                au trou, pas au score, et ils se lisent que la
                                carte soit en brut ou en net — c'est même en brut
                                qu'ils renseignent le plus. */}
                            <PointsRendus n={rendu} sombre={sombre} />
                        </Case>
                    );
                })}
            </div>

            {/* Le total, hors du défilement : c'est le chiffre qu'on cherche en
                premier, il ne doit pas partir avec le ruban. */}
            <div className={`flex shrink-0 flex-col items-center justify-center border-l pl-2 ${
                sombre ? 'border-white/15 text-white' : 'border-base-300'}`}>
                <span className={`text-[10px] uppercase leading-none tracking-wide ${
                    sombre ? 'text-white/55' : 'opacity-60'}`}>tot</span>
                <span className="font-score text-base leading-none">
                    {total > 0 ? total : '—'}
                </span>
                {parJoue > 0 && (
                    <span className={`text-[10px] font-semibold leading-none tabular-nums ${
                        total > parJoue
                            ? sombre ? 'text-[color:var(--score-perdu)]' : 'text-error'
                            : total < parJoue
                                ? sombre ? 'text-[color:var(--score-gagne)]' : 'text-success'
                                : sombre ? 'text-white/60' : 'opacity-60'
                    }`}>
                        {total === parJoue ? 'par' : `${total > parJoue ? '+' : '−'}${Math.abs(total - parJoue)}`}
                    </span>
                )}
            </div>
        </div>
    );
}
